var _a;
import { ERROR_CODE, PLAY_EVENT, VIDEO_ELEMENT_STATE, VIDEO_EVTS, VIDEO_NODE_EVENT, VIDEO_TYPE, } from '../enums';
import { i18nConfig } from '../i18n';
import KernelFactory from '../kernel-selector/kernel-factory';
import { t, urlTool } from '../util';
/**
 * 对比两个UrlConfig是否是同一个视频节点
 */
export function compareUrlConfig(a, b) {
    if (!a || !b) {
        return false;
    }
    // 如果有uid，先对比uid
    if (a.uid && b.uid && a.uid === b.uid) {
        return true;
    }
    // 然后直接对比UrlConfig对象的引用
    if (a === b) {
        return true;
    }
    return false;
}
/**
 * 通过后缀名判断VideoType，用于判断内核
 * @param extension 文件后缀名
 * @returns 返回对应VIDEO_TYPE
 */
export function fileExtension2VideoType(extension) {
    var lcExtension = extension === null || extension === void 0 ? void 0 : extension.toLocaleLowerCase();
    // 通过后缀名判断VideoType，用于判断内核
    switch (lcExtension) {
        case 'm3u8':
        case 'm3u':
            return VIDEO_TYPE.HLS;
        case 'mp4':
        case 'mov':
            return VIDEO_TYPE.MP4;
        case 'flv':
        case 'f4v':
            return VIDEO_TYPE.FLV;
        case 'avi':
            return VIDEO_TYPE.AVI;
        case 'mpeg':
            return VIDEO_TYPE.MPEG;
        case 'webm':
            return VIDEO_TYPE.WEBM;
        case 'ogg':
            return VIDEO_TYPE.OGG;
    }
    return VIDEO_TYPE.OTHER;
}
/**
 * 检查UrlConfig作为参数时是否是合法的。例如UrlConfig的url参数为空，则会返回错误。
 * @param value 地址信息（或列表）
 * @param kernelName 播放内核名称
 * @returns 如果不能播放返回错误信息，如果可以播放返回undefined
 */
export function checkUrlConfigVailed(value, kernelName) {
    var _a;
    if (!value) {
        return { code: ERROR_CODE.NULL_URL, message: t(i18nConfig.core.ERROR_NULL_URL), fatal: true };
    }
    // 先拿到所有可以支持的视频类型列表
    var canPlayTypes = (_a = KernelFactory.getKernelByName(kernelName)) === null || _a === void 0 ? void 0 : _a.canPlayType;
    var vailed = function (conf, vailedTypes) {
        // 先判断是否有空url
        var nullUrl = !conf.url;
        if (nullUrl) {
            return { code: ERROR_CODE.NULL_URL, message: t(i18nConfig.core.ERROR_NULL_URL), fatal: true };
        }
        return;
    };
    if (value instanceof Array) {
        // UrlConfig数组中只要有一个通不过验证，则返回那个错误信息。否则返回void 0
        for (var i = 0, len = value.length; i < len; i++) {
            var error = vailed(value[i], canPlayTypes);
            if (error) {
                return error;
            }
        }
        return;
    }
    return vailed(value, canPlayTypes);
}
/**
 * 根据UrlConfig的地址拿到后缀，然后判断视频类型
 * @returns 返回视频类型的字符串，例如mp4，或者hls等
 */
export function getUrlConfigVideoType(urlConfig) {
    var url = (urlConfig || {}).url;
    if (!url || url.startsWith('blob:')) {
        return VIDEO_TYPE.OTHER;
    }
    if (url.startsWith('webrtc:')) {
        return VIDEO_TYPE.WEBRTC;
    }
    var extension = urlTool.getFileExtension(url);
    var videoType = fileExtension2VideoType(extension);
    return videoType;
}
/**
 * 从config传入的video标签数组查找未使用的video标签（是否在文档流中）
 * @param videoElements video元素对象
 * @returns 空闲的IVideoElement
 */
export function findFreeBaseVideoElement(videoElements) {
    if (!videoElements || videoElements.length === 0) {
        throw new Error('videoElements is null or length equal 0');
    }
    var freeVideoElement = null;
    for (var i = 0; i < videoElements.length; i++) {
        var el = videoElements[i];
        if (typeof el.dataset.used === 'undefined' || el.dataset.used === VIDEO_ELEMENT_STATE.FREE) {
            freeVideoElement = videoElements[i];
            break;
        }
    }
    if (!freeVideoElement) {
        throw new Error('Unable to find free video element!');
    }
    freeVideoElement.dataset.used = VIDEO_ELEMENT_STATE.USED;
    return freeVideoElement;
}
/** videonode的事件到播放器事件的映射 */
export var videoEvt2PlayerEvtMap = (_a = {},
    _a[VIDEO_NODE_EVENT.DURATION_CHANGE] = PLAY_EVENT.DURATION_CHANGE,
    _a[VIDEO_NODE_EVENT.VOLUME_CHANGE] = PLAY_EVENT.VOLUME_CHANGE,
    _a[VIDEO_NODE_EVENT.ERROR] = PLAY_EVENT.ERROR,
    _a[VIDEO_NODE_EVENT.METADATA_CHANGE] = PLAY_EVENT.METADATA_CHANGE,
    _a[VIDEO_NODE_EVENT.SETLEVEL_OVER] = PLAY_EVENT.SETLEVEL_OVER,
    _a[VIDEO_NODE_EVENT.SETLEVEL_START] = PLAY_EVENT.SETLEVEL_START,
    _a[VIDEO_NODE_EVENT.SET_LOADING] = PLAY_EVENT.SET_LOADING,
    _a[VIDEO_NODE_EVENT.STATE_CHANGE] = PLAY_EVENT.STATE_CHANGE,
    _a[VIDEO_NODE_EVENT.TIME_UPDATE] = PLAY_EVENT.TIME_UPDATE,
    _a[VIDEO_NODE_EVENT.VIDEO_BUFFER_CHANGE] = PLAY_EVENT.VIDEO_BUFFER_CHANGE,
    _a[VIDEO_NODE_EVENT.LOAD_PROGRESS] = PLAY_EVENT.LOAD_PROGRESS,
    _a[VIDEO_NODE_EVENT.FIRST_PLAYING] = PLAY_EVENT.VIDEONODE_FIRST_PLAYING,
    _a[VIDEO_NODE_EVENT.RATE_CHANGE] = PLAY_EVENT.RATE_CHANGE,
    _a[VIDEO_NODE_EVENT.RETRY] = PLAY_EVENT.RETRY_BACKURL,
    _a[VIDEO_NODE_EVENT.ELEMENT_EVTS] = PLAY_EVENT.ELEMENT_EVTS,
    _a[VIDEO_NODE_EVENT.PLAY_OVER] = PLAY_EVENT.STOPED,
    _a[VIDEO_NODE_EVENT.VIDEO_RESIZE] = PLAY_EVENT.VIDEO_RESIZE,
    _a[VIDEO_NODE_EVENT.VIDEO_FRAME_RENDER] = PLAY_EVENT.VIDEO_FRAME_RENDER,
    _a[VIDEO_NODE_EVENT.SET_AUDIO_TRACK_OVER] = PLAY_EVENT.SET_AUDIO_TRACK_OVER,
    _a[VIDEO_NODE_EVENT.SET_AUDIO_TRACK_START] = PLAY_EVENT.SET_AUDIO_TRACK_START,
    _a[VIDEO_NODE_EVENT.ENTER_INSERT_STREAM] = PLAY_EVENT.ENTER_INSERT_STREAM,
    _a[VIDEO_NODE_EVENT.EXIT_INSERT_STREAM] = PLAY_EVENT.EXIT_INSERT_STREAM,
    _a);
// VideoNode 抛出这些事件，因为过于频繁，所以不打印
export var IGNORE_EVT_TYPE = [
    VIDEO_NODE_EVENT.TIME_UPDATE,
    VIDEO_NODE_EVENT.VIDEO_BUFFER_CHANGE,
    VIDEO_NODE_EVENT.VIDEO_FRAME_RENDER,
];
// IVideoElement 抛出这些事件，因为过于频繁，所以不打印
export var IGNORE_VIDEO_EVT = [
    VIDEO_EVTS.timeupdate,
    VIDEO_EVTS.progress,
    VIDEO_EVTS.frameRender,
];
